<template>
    <div class="pano-360" :class="{ 'fullscreen': fullscreen, 'pano-360-preview': preview, 'hide-actions': hideActions, 'pano-360-ar-mode': modeAR, 'pano-360-image-only': imageOnly }">
        <map-destination-reached v-if="isArrived && !preview && !imageOnly"/>

        <div class="pano-360-container rounded" :style="{ pointerEvents: modeAR ? 'none' : 'auto' }"
             ref="pano-360-container" @pointerdown="onUserInteract" @click="onContainerClick"
             @contextmenu.prevent @selectstart.prevent @dragstart.prevent>
            <div v-if="isTouchAnimationVisible && !imageOnly">
                <img class="animated-finger" src="@/assets/icons/touch-hint.svg"/>
            </div>
        </div>
        <div v-if="isPanoLoading && !imageOnly" class="pano-loading-overlay"/>
        <div v-if="flyingArrow && !imageOnly" class="pano-flying-arrow" :style="flyingArrowStyle">
            <div class="pano-flying-arrow-wrapper">
                <div class="pano-flying-arrow-disc" :style="flyingArrowDiscStyle">
                    <img class="pano-flying-arrow-head" :src="arrowHeadSrc" alt="" draggable="false">
                </div>
            </div>
        </div>
        <div v-if="showSideIndicatorsLayer && !imageOnly" class="pano-side-indicators" :class="{ 'is-visible': sideIndicatorsVisible }">
            <div v-if="activeSideIndicator === `left`" class="pano-side-indicator is-left">
                <div class="pano-side-indicator-disc" :style="sideIndicatorDiscStyle">
                    <div class="pano-side-indicator-chevron"/>
                    <div class="pano-side-indicator-chevron is-trail"/>
                </div>
            </div>
            <div v-if="activeSideIndicator === `right`" class="pano-side-indicator is-right">
                <div class="pano-side-indicator-disc" :style="sideIndicatorDiscStyle">
                    <div class="pano-side-indicator-chevron"/>
                    <div class="pano-side-indicator-chevron is-trail"/>
                </div>
            </div>
        </div>

        <div class="debug" v-if="isPanoDebugVisible && viewer && !imageOnly">
            <b-badge class="mb-1">Yaw: {{ viewerYaw|round }}</b-badge>
            <b-badge class="mb-1">Pitch: {{ viewerPitch|round }}</b-badge>
            <b-badge class="mb-1">HFov: {{ viewerHfov|round }}</b-badge>
            <b-badge class="mb-1">Camera: {{ cameraAngle|round }}</b-badge>
            <b-badge class="mb-1">Image: {{ imgAngle|round }}</b-badge>
            <b-badge class="mb-1">From: {{ fromAngle|round }}</b-badge>
            <b-badge class="mb-1">To: {{ toAngle|round }}</b-badge>
        </div>
    </div>
</template>

<script>
import MapDestinationReached from "@/components/maps/map-destination-reached.vue";
import MapMixin from "@/helpers/maps/map-mixin.js";
import MapMathHelper from "@/helpers/maps/MapMathHelper.js";
import "pannellum";
import "pannellum/build/pannellum.css";
import ArrowHeadSvg from "@/assets/arrow-head.svg";
import LocationSvg from "@/assets/icons/location.svg";

const TOUCH_HINT_STORAGE_KEY = `touchAnimationShown`;
const NAV_HOTSPOT_ID = `nav-arrow`;
const FROM_NAV_HOTSPOT_ID = `nav-arrow-from`;
const GUIDE_HOTSPOT_ID_PREFIX = `nav-guide-`;
const SIDE_HOTSPOT_LEFT_ID = `nav-side-left`;
const SIDE_HOTSPOT_RIGHT_ID = `nav-side-right`;
const INTRO_ANIMATION_DURATION = 2500;
const DEFAULT_ARROW_COLOR = `#2297ff`;
const CAMERA_YAW_EPSILON_DEG = 0.35;
const ARROW_TRANSITION_BASE_DURATION = 1800;
const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;
const PATH_DOT_EPSILON = 0.01;
const GUIDE_REVEAL_STEP_MS = 90;
const INTRO_MAIN_POP_DELAY_MS = 50;
const ARROW_FADE_OUT_MS = 180;
const MIN_INTRO_ANIMATION_TURN_DEG = 12;
const INTRO_REVEAL_ANIM_MS = 160;
const INTRO_REVEAL_SPRING = Object.freeze({mass: 1, damping: 10, stiffness: 100});
const SIDE_INDICATOR_REFRESH_MS = 90;
const SIDE_INDICATOR_SCREEN_MARGIN_PX = 18;
const SIDE_INDICATOR_SIZE_PX = 96;
const SIDE_INDICATOR_EDGE_OFFSET_PX = 40;
const SIDE_INDICATOR_HIT_RADIUS_PX = 42;
const CAMERA_RECENTER_DURATION_MS = 850;
const CAMERA_PITCH_EPSILON_DEG = 0.35;
const BACK_MARKER_COLOR = `#9ea6b2`;
const MIN_PITCH_DEG = -90;
const MAX_PITCH_DEG = 90;
const AR_TARGET_HFOV = 70;
const WIDEST_HFOV = 65;
const MOST_ZOOMED_HFOV = 120;
const FULLSCREEN_DEFAULT_HFOV = MOST_ZOOMED_HFOV;
const MOBILE_DEFAULT_HFOV = 100;
const GUIDE_PITCH_STEP_HEIGHT_RATIO_MOBILE = 0.12;
const GUIDE_PITCH_STEP_HEIGHT_RATIO_DESKTOP = 0.08;
const HFOV_EPSILON_DEG = 0.05;
const AR_DEFAULT_PITCH_OFFSET_DEG = 35;

function createQuaternion(w, x, y, z) {
    return {w, x, y, z};
}

function multiplyQuaternion(left, right) {
    return createQuaternion(
        left.w * right.w - left.x * right.x - left.y * right.y - left.z * right.z,
        left.x * right.w + left.w * right.x + left.y * right.z - left.z * right.y,
        left.y * right.w + left.w * right.y + left.z * right.x - left.x * right.z,
        left.z * right.w + left.w * right.z + left.x * right.y - left.y * right.x,
    );
}

function quaternionToEulerAngles(quaternion) {
    return [
        Math.atan2(2 * (quaternion.w * quaternion.x + quaternion.y * quaternion.z), 1 - 2 * (quaternion.x * quaternion.x + quaternion.y * quaternion.y)),
        Math.asin(2 * (quaternion.w * quaternion.y - quaternion.z * quaternion.x)),
        Math.atan2(2 * (quaternion.w * quaternion.z + quaternion.x * quaternion.y), 1 - 2 * (quaternion.y * quaternion.y + quaternion.z * quaternion.z)),
    ];
}

function getScreenOrientationAngle() {
    if (window.screen && window.screen.orientation && window.screen.orientation.angle != null) {
        return window.screen.orientation.angle;
    }
    if (window.orientation != null) {
        return window.orientation;
    }
    return 0;
}

function getDeviceOrientationEulerAngles(event) {
    const halfBeta = (event.beta || 0) * DEG_TO_RAD * 0.5;
    const halfGamma = (event.gamma || 0) * DEG_TO_RAD * 0.5;
    const halfAlpha = (event.alpha || 0) * DEG_TO_RAD * 0.5;
    const cosBeta = Math.cos(halfBeta);
    const cosGamma = Math.cos(halfGamma);
    const cosAlpha = Math.cos(halfAlpha);
    const sinBeta = Math.sin(halfBeta);
    const sinGamma = Math.sin(halfGamma);
    const sinAlpha = Math.sin(halfAlpha);
    let quaternion = createQuaternion(
        cosBeta * cosGamma * cosAlpha - sinBeta * sinGamma * sinAlpha,
        sinBeta * cosGamma * cosAlpha - cosBeta * sinGamma * sinAlpha,
        cosBeta * sinGamma * cosAlpha + sinBeta * cosGamma * sinAlpha,
        cosBeta * cosGamma * sinAlpha + sinBeta * sinGamma * cosAlpha,
    );
    quaternion = multiplyQuaternion(quaternion, createQuaternion(Math.sqrt(0.5), -Math.sqrt(0.5), 0, 0));
    const screenHalfAngle = -getScreenOrientationAngle() * DEG_TO_RAD * 0.5;
    quaternion = multiplyQuaternion(quaternion, createQuaternion(Math.cos(screenHalfAngle), 0, -Math.sin(screenHalfAngle), 0));
    return quaternionToEulerAngles(quaternion);
}

export default {
    name: `pano-360-pannellum`,
    components: {MapDestinationReached},
    mixins: [MapMixin],
    props: {
        currentInstruction: {type: Object, default: null},
        src: {type: String, default: ``},
        isArrived: {type: Boolean},
        fullscreen: {type: Boolean},
        gridMode: {type: Boolean},
        gridModeGlyph: {type: String, default: `fa-location-crosshairs`},
        preview: {type: Boolean, default: false},
        arrowColor: {type: String, default: DEFAULT_ARROW_COLOR},
        modeAr: {type: Boolean, default: false},
        isMobilePitchEnabled: {type: Boolean, default: true},
        isFirstStep: {type: Boolean, default: false},
        hideActions: {type: Boolean, default: false},
        imageOnly: {type: Boolean, default: false},
        animation: {type: Boolean, default: true},
        disableZoom: {type: Boolean, default: false},
        autoRotate: {type: Boolean, default: false},
    },
    data() {
        return {
            container: null,
            viewer: null,
            viewerYaw: 0,
            viewerPitch: 0,
            viewerHfov: 100,
            cameraAngle: 0,
            modeAR: false,
            arPoseSnapshot: null,
            cameraAnimationCancelled: false,
            touchAnimationHidden: false,
            touchAnimationAlreadyShown: false,
            panoramaLoadToken: 0,
            resizeObserver: null,
            boundWindowResize: null,
            boundDeviceOrientationChange: null,
            viewerEvents: [],
            isDestroyed: false,
            avifSupportPromise: null,
            webpSupport: null,
            isPanoLoading: false,
            isIntroAnimating: false,
            flyingArrow: null,
            arrowTransitionActive: false,
            arrowTransitionRafId: null,
            flyingArrowState: {x: 0, y: 0, scale: 1, opacity: 1, rotation: 0},
            projectionStateCache: {
                width: 0,
                height: 0,
                halfWidth: 0,
                halfHeight: 0,
                focal: 0,
                vSinYaw: 0,
                vCosYaw: 1,
                vSinPitch: 0,
                vCosPitch: 1,
            },
            arrowProjectScratchA: {x: 0, y: 0},
            arrowProjectScratchB: {x: 0, y: 0},
            guideHotspotIds: [],
            sideHotspotLeftExists: false,
            sideHotspotRightExists: false,
            introAnimationComplete: false,
            introRevealTimeoutIds: [],
            sideIndicatorsVisible: false,
            sideIndicatorRefreshIntervalId: null,
            activeSideIndicator: null,
            customOrientationListening: false,
            customOrientationYawOffset: 0,
            lastDeviceOrientationYaw: null,
            arrowHeadSrc: ArrowHeadSvg,
            locationIconSrc: LocationSvg,
        }
    },
    computed: {
        panoramaSrc() {
            if (this.currentInstruction?.image360) {
                return this.currentInstruction.image360;
            }
            return this.src;
        },
        imgAngle() {
            if (this.currentInstruction) {
                return this.currentInstruction.offsetAngle;
            }
            return 0;
        },
        imgPitch() {
            if (this.currentInstruction?.imgPitch) {
                return this.currentInstruction.imgPitch;
            }
            return 0;
        },
        fromAngle() {
            if (this.currentInstruction) {
                return this.currentInstruction.fromAngle;
            }
            return 0;
        },
        fromHotspotAngle() {
            if (this.currentInstruction?.isFromHotspotAngleAbsolute) {
                return this.currentInstruction.fromHotspotAngle;
            }
            return this.fromAngle;
        },
        toAngle() {
            if (this.currentInstruction) {
                return this.currentInstruction.toAngle;
            }
            return 0;
        },
        isFromHotspotAngleAbsolute() {
            if (this.currentInstruction) {
                return this.currentInstruction.isFromHotspotAngleAbsolute;
            }
            return false;
        },
        isToAngleAbsolute() {
            if (this.currentInstruction) {
                return this.currentInstruction.isToAngleAbsolute;
            }
            return false;
        },
        normalizedFromAngle() {
            return MapMathHelper.normalizeAngle(this.fromAngle);
        },
        normalizedFromHotspotAngle() {
            if (this.isFromHotspotAngleAbsolute) {
                return MapMathHelper.normalizeAngle(this.fromHotspotAngle - this.getImageOffset());
            }
            return MapMathHelper.normalizeAngle(this.fromAngle + 180);
        },
        normalizedArrowAngle() {
            if (this.isToAngleAbsolute) {
                return MapMathHelper.normalizeAngle(this.toAngle - this.getImageOffset());
            }
            return MapMathHelper.normalizeAngle(this.toAngle);
        },
        normalizedFromProlongementAngle() {
            return this.normalizedFromAngle;
        },
        flyingArrowStyle() {
            if (!this.flyingArrow) {
                return {};
            }
            return {
                transform: `translate(${this.flyingArrow.x}px, ${this.flyingArrow.y}px) translate(-50%, -50%) rotate(${this.flyingArrow.rotation}deg) scale(${this.flyingArrow.scale})`,
                opacity: this.flyingArrow.opacity,
            };
        },
        flyingArrowDiscStyle() {
            const color = this.arrowColor;
            return {
                background: `radial-gradient(circle, ${this.colorWithAlpha(color, 0.85)} 0%, ${this.colorWithAlpha(color, 0.65)} 50%, ${this.colorWithAlpha(color, 0.35)} 80%, ${this.colorWithAlpha(color, 0.2)} 100%)`,
                boxShadow: `0 0 14px 2px ${this.colorWithAlpha(color, 0.22)}`,
            };
        },
        sideIndicatorDiscStyle() {
            const color = this.arrowColor;
            return {
                background: `radial-gradient(circle, ${this.colorWithAlpha(color, 1)} 0%, ${this.colorWithAlpha(color, 0.85)} 50%, ${this.colorWithAlpha(color, 0.55)} 80%, ${this.colorWithAlpha(color, 0.4)} 100%)`,
                border: `2px solid rgba(255, 255, 255, 0.9)`,
                boxShadow: `0 0 18px 4px ${this.colorWithAlpha(color, 0.25)}`,
            };
        },
        isTouchAnimationVisible() {
            if (isKioskDevice) {
                return this.isFirstStep && !this.touchAnimationHidden;
            }
            return this.isFirstStep && !this.touchAnimationHidden && !this.touchAnimationAlreadyShown;
        },
        showSideIndicatorsLayer() {
            return Boolean(this.viewer && !this.isMobilePreview && !this.isArrived);
        },
        isMobilePreview() {
            return this.preview && this.isMobileDevice;
        }
    },
    created() {
        if (!isKioskDevice) {
            this.touchAnimationAlreadyShown = localStorage.getItem(TOUCH_HINT_STORAGE_KEY) === `true`;
            if (this.touchAnimationAlreadyShown) {
                this.touchAnimationHidden = true;
            }
        }
        // Warm SVG assets early to avoid delayed mask/image first-paint on Safari.
        [ArrowHeadSvg, LocationSvg].forEach((src) => {
            const image = new Image();
            image.src = src;
        });
    },
    mounted() {
        this.modeAR = Boolean(this.modeAr);
        this.container = this.$refs[`pano-360-container`];
        this.boundWindowResize = () => this.updateContainerSize();
        this.boundDeviceOrientationChange = this.onDeviceOrientationChange.bind(this);
        window.addEventListener(`resize`, this.boundWindowResize);
        this.initResizeObserver();
        this.updateContainerSize();
        this.initializeViewer({animateIntro: !this.isMobilePreview});
    },
    methods: {
        getPannellum() {
            return window.pannellum;
        },
        normalizeSigned(angle) {
            const normalized = ((Number(angle) + 180) % 360 + 360) % 360 - 180;
            return normalized === -180 ? 180 : normalized;
        },
        getImageOffset() {
            return Number(this.imgAngle) + this.currentMap.get360ImagesOffsetAngle();
        },
        getPitchOffset() {
            if (this.modeAR && !this.isMobilePitchEnabled) {
                return 0;
            }
            if (this.modeAR && this.imgPitch === 0) {
                return AR_DEFAULT_PITCH_OFFSET_DEG;
            }
            return this.imgPitch;
        },
        toViewerYaw(worldAngle) {
            return this.normalizeSigned(Number(worldAngle) + this.getImageOffset());
        },
        toWorldAngle(viewerYaw) {
            return this.normalizeSigned(Number(viewerYaw) - this.getImageOffset());
        },
        getTurnDeltaDegrees(fromAngle = this.normalizedFromProlongementAngle, toAngle = this.normalizedArrowAngle) {
            return Math.abs(this.normalizeSigned(Number(toAngle) - Number(fromAngle)));
        },
        isIntroPopRevealAllowed() {
            return Boolean(!this.gridMode && !this.isMobilePreview && !this.isDestroyed && this.animation && !this.isFirstStep);
        },
        canPlayIntroPopReveal() {
            return Boolean(this.viewer && this.isIntroPopRevealAllowed());
        },
        shouldTriggerPopOnSmallTurn(fromAngle = this.normalizedFromProlongementAngle, toAngle = this.normalizedArrowAngle) {
            return this.isIntroPopRevealAllowed() && this.getTurnDeltaDegrees(fromAngle, toAngle) < MIN_INTRO_ANIMATION_TURN_DEG;
        },
        shouldSkipElementAnimations() {
            if (this.isFirstStep) {
                return true;
            }
            return this.getTurnDeltaDegrees() < MIN_INTRO_ANIMATION_TURN_DEG;
        },
        isMotionAnimationDisabled() {
            return Boolean(this.modeAR);
        },
        replacePanoFilename(url, targetFilename) {
            if (!url) {
                return url;
            }
            return url.replace(/\/(file(?:-mobile)?\.avif|thumbnail(?:-large)?\.webp)(\?.*)?$/, `/${targetFilename}$2`);
        },
        isAvifSupported() {
            if (!this.avifSupportPromise) {
                this.avifSupportPromise = new Promise((resolve) => {
                    const avif = new Image();
                    avif.onload = () => resolve(true);
                    avif.onerror = () => resolve(false);
                    avif.src = `data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgANogQEAwgMg8f8D///8WfhwB8+ErK42A=`;
                });
            }
            return this.avifSupportPromise;
        },
        isWebpSupported() {
            if (this.webpSupport === null) {
                this.webpSupport = document.createElement(`canvas`).toDataURL(`image/webp`).indexOf(`data:image/webp`) === 0;
            }
            return this.webpSupport;
        },
        async resolvePanoramaUrl() {
            const isMobileRuntime = this.isMobileDevice;
            const supportsAvif = await this.isAvifSupported();
            if (supportsAvif) {
                if (this.preview) {
                    return this.replacePanoFilename(this.panoramaSrc, isMobileRuntime ? `thumbnail-large.webp` : `file-mobile.avif`);
                }
                if (isMobileRuntime) {
                    return this.replacePanoFilename(this.panoramaSrc, `file-mobile.avif`);
                }
                if (this.fullscreen) {
                    return this.panoramaSrc;
                }
                return this.replacePanoFilename(this.panoramaSrc, `file-mobile.avif`);
            }

            const fallbackUrl = this.replacePanoFilename(this.panoramaSrc, `thumbnail-large.webp`);
            if (fallbackUrl !== this.panoramaSrc && this.isWebpSupported()) {
                return fallbackUrl;
            }

            if (fallbackUrl !== this.panoramaSrc && !this.isWebpSupported()) {
                alert(`Your browser does not support WebP format. Please use a modern browser.`);
                return null;
            }

            return this.panoramaSrc;
        },
        initResizeObserver() {
            if (!this.container) {
                return;
            }
            this.resizeObserver = new ResizeObserver(() => {
                this.resize();
            });
            this.resizeObserver.observe(this.container);
        },
        updateLoadingStateFromViewer() {
            if (!this.viewer) {
                this.isPanoLoading = false;
                return false;
            }

            const renderer = this.viewer.getRenderer();
            this.isPanoLoading = renderer.isLoading();

            return this.isPanoLoading;
        },
        safeViewerOff(viewerRef, eventName, handler) {
            viewerRef.off(eventName, handler);
        },
        registerViewerEvent(eventName, handler) {
            if (!this.viewer) {
                return;
            }
            this.viewer.on(eventName, handler);
            this.viewerEvents?.push({eventName, handler});
        },
        removeViewerEvents() {
            if (!this.viewer) {
                this.viewerEvents = [];
                return;
            }
            for (const entry of this.viewerEvents) {
                this.safeViewerOff(this.viewer, entry.eventName, entry.handler);
            }
            this.viewerEvents = [];
        },
        bindViewerEvents() {
            this.removeViewerEvents();

            this.registerViewerEvent(`load`, () => {
                this.syncViewStateFromViewer();
                this.updateLoadingStateFromViewer();
                if (!this.isIntroAnimating) {
                    this.updateHotspots();
                }
                this.refreshSideIndicatorsVisibility();
            });
            this.registerViewerEvent(`zoomchange`, () => {
                this.syncViewStateFromViewer();
                if (this.modeAR) {
                    this.enforceArTargetHfov();
                }
                this.refreshSideIndicatorsVisibility();
            });
            this.registerViewerEvent(`animatefinished`, () => {
                this.syncViewStateFromViewer();
                this.refreshSideIndicatorsVisibility();
            });
            this.registerViewerEvent(`mouseup`, () => {
                this.syncViewStateFromViewer();
                this.refreshSideIndicatorsVisibility();
            });
            this.registerViewerEvent(`touchend`, () => {
                this.syncViewStateFromViewer();
                this.refreshSideIndicatorsVisibility();
            });
            this.registerViewerEvent(`mousedown`, () => {
                this.cancelIntroAnimation();
                this.refreshSideIndicatorsVisibility();
            });
            this.registerViewerEvent(`touchstart`, () => {
                this.cancelIntroAnimation();
                this.refreshSideIndicatorsVisibility();
            });
        },
        async waitForViewerLoad(timeout = 6000) {
            if (!this.viewer) {
                return false;
            }
            if (this.viewer.isLoaded()) {
                return true;
            }

            const viewerRef = this.viewer;

            const pannellumMsgSpan = document.querySelector(`.pnlm-about-msg`);

            if (pannellumMsgSpan) {
                pannellumMsgSpan.remove();
            }

            return new Promise((resolve) => {
                let settled = false;
                let timer = null;

                const finish = (loaded) => {
                    if (settled) {
                        return;
                    }
                    settled = true;
                    clearTimeout(timer);
                    this.safeViewerOff(viewerRef, `load`, onLoad);
                    this.safeViewerOff(viewerRef, `error`, onError);
                    resolve(Boolean(loaded));
                };

                const onLoad = () => finish(true);
                const onError = () => finish(false);

                viewerRef.on(`load`, onLoad);
                viewerRef.on(`error`, onError);

                timer = setTimeout(() => finish(viewerRef.isLoaded()), timeout);
            });
        },
        getInitialCameraAngle() {
            if (this.modeAR) {
                return this.normalizeSigned(this.cameraAngle);
            }
            if (this.isMobilePreview || this.isFirstStep) {
                return this.normalizeSigned(this.normalizedArrowAngle);
            }
            return this.normalizeSigned(this.normalizedFromProlongementAngle);
        },
        getSceneConfig(panoramaUrl) {
            const isMobileRuntime = this.isMobileDevice;
            const lockPreview = this.isMobilePreview && this.isMobileLayoutOrPreview;
            let hfov = isMobileRuntime ? MOBILE_DEFAULT_HFOV : WIDEST_HFOV;
            if (this.isMobilePreview) {
                hfov = WIDEST_HFOV;
            }
            if (this.fullscreen) {
                hfov = FULLSCREEN_DEFAULT_HFOV;
            }
            const minHfov = isMobileRuntime ? hfov : WIDEST_HFOV;
            const maxHfovBound = isMobileRuntime ? hfov : MOST_ZOOMED_HFOV;
            const zoomEnabled = !lockPreview && !isMobileRuntime && !this.disableZoom;
            const initialCameraAngle = this.getInitialCameraAngle();
            return {
                type: `equirectangular`,
                panorama: panoramaUrl,
                autoLoad: true,
                showControls: false,
                draggable: !lockPreview,
                yaw: this.toViewerYaw(initialCameraAngle),
                pitch: this.getPitchOffset(),
                hfov,
                minHfov,
                maxHfov: maxHfovBound,
                minPitch: MIN_PITCH_DEG,
                maxPitch: MAX_PITCH_DEG,
                mouseZoom: zoomEnabled,
                keyboardZoom: zoomEnabled,
                doubleClickZoom: zoomEnabled,
                friction: isMobileRuntime ? 0.12 : 0.15,
                touchPanSpeedCoeffFactor: lockPreview ? 0 : 1,
            };
        },
        async initializeViewer({animateIntro = true, preserveArPose = true} = {}) {
            const loadToken = ++this.panoramaLoadToken;
            const panoramaUrl = await this.resolvePanoramaUrl();

            if (this.isDestroyed || loadToken !== this.panoramaLoadToken || !panoramaUrl) {
                return;
            }

            const pannellumApi = this.getPannellum();
            const sceneConfig = this.getSceneConfig(panoramaUrl);
            this.isPanoLoading = true;
            const shouldAnimateIntro = Boolean(
                animateIntro
                && !this.gridMode
                && this.animation
                && !this.isMobilePreview
                && !this.shouldSkipElementAnimations()
                && !this.isMotionAnimationDisabled()
            );
            const shouldPopAfterSkippedFly = Boolean(
                animateIntro
                && !this.gridMode
                && this.shouldTriggerPopOnSmallTurn(this.normalizedFromProlongementAngle, this.normalizedArrowAngle)
                && !this.isMotionAnimationDisabled()
            );
            this.isIntroAnimating = shouldAnimateIntro;
            this.introAnimationComplete = !shouldAnimateIntro;
            this.clearIntroRevealSequence();

            this.destroyViewer();
            if (!preserveArPose) {
                const preservedRoll = Number(this.arPoseSnapshot?.roll);
                this.arPoseSnapshot = Number.isFinite(preservedRoll)
                    ? {yaw: null, pitch: null, roll: preservedRoll}
                    : null;
            }
            this.viewer = pannellumApi.viewer(this.container, sceneConfig);
            this.bindViewerEvents();
            this.updateContainerSize();

            const loaded = await this.waitForViewerLoad();
            if (!loaded || this.isDestroyed || loadToken !== this.panoramaLoadToken || !this.viewer) {
                this.isPanoLoading = false;
                return;
            }
            this.updateLoadingStateFromViewer();

            const initialCameraAngle = this.getInitialCameraAngle();
            this.cameraAnimationCancelled = false;
            this.cameraAngle = initialCameraAngle;
            this.viewer.setPitch(this.getPitchOffset(), false);
            this.viewer.setPitchBounds([MIN_PITCH_DEG, MAX_PITCH_DEG]);
            this.restoreArPoseSnapshotIfNeeded();
            this.syncViewStateFromViewer();
            if (this.modeAR) {
                this.activateArMode();
            }
            this.startSideIndicatorVisibilityTracking();

            // Avoid overriding DeviceOrientation baseline in AR.
            if (!this.modeAR) {
                // Ensure a deterministic intro start point on each scene / src switch.
                this.setCameraAngle(this.cameraAngle, {force: true});
            }

            if (shouldAnimateIntro) {
                this.isIntroAnimating = true;
                this.animateArrowHotspotTransition();
                this.animateCameraToAngle(this.normalizedArrowAngle, INTRO_ANIMATION_DURATION, () => {
                    this.isIntroAnimating = false;
                });
            } else {
                this.isIntroAnimating = false;
                if (!this.modeAR) {
                    this.setCameraAngle(this.cameraAngle, {force: true});
                    if (this.autoRotate) {
                        this.viewer.startAutoRotate(10);
                    }
                }
                if (shouldPopAfterSkippedFly) {
                    this.runIntroRevealSequence();
                } else {
                    this.updateHotspots();
                }
            }
            this.refreshSideIndicatorsVisibility();
        },
        destroyViewer() {
            this.cancelArrowHotspotTransition({restoreHotspot: false});
            if (!this.viewer) {
                this.stopOrientation();
                this.stopSideIndicatorVisibilityTracking();
                this.setSideIndicatorsVisible(false);
                return;
            }

            this.captureArPoseSnapshotIfNeeded();
            this.stopOrientation();
            this.removeViewerEvents();
            this.removeNavigationHotspots();
            this.stopSideIndicatorVisibilityTracking();
            this.setSideIndicatorsVisible(false);
            this.viewer.destroy();
            this.viewer = null;
        },
        captureArPoseSnapshotIfNeeded() {
            if (!this.modeAR || !this.viewer) {
                return;
            }
            const config = this.viewer.getConfig?.();
            const roll = Number(config?.roll);
            this.arPoseSnapshot = {
                yaw: Number(this.viewer.getYaw()),
                pitch: Number(this.viewer.getPitch()),
                roll: Number.isFinite(roll) ? roll : 0,
            };
        },
        restoreArPoseSnapshotIfNeeded() {
            if (!this.modeAR || !this.viewer || !this.arPoseSnapshot) {
                return;
            }
            const {yaw, pitch, roll} = this.arPoseSnapshot;
            if (Number.isFinite(yaw)) {
                this.viewer.setYaw(yaw, false);
            }
            if (Number.isFinite(pitch)) {
                this.viewer.setPitch(pitch, false);
            }
            if (Number.isFinite(roll) && this.viewer.getConfig) {
                this.viewer.getConfig().roll = roll;
            }
        },
        isDeviceOrientationSupported() {
            if (!this.isMobilePitchEnabled) {
                if (!this.viewer) {
                    return false;
                }
                return Boolean(this.viewer.isOrientationSupported());
            }
            return Boolean(window.DeviceOrientationEvent);
        },
        isDeviceOrientationActive() {
            if (!this.isMobilePitchEnabled) {
                if (!this.viewer) {
                    return false;
                }
                return Boolean(this.viewer.isOrientationActive());
            }
            return this.customOrientationListening;
        },
        startOffsetDeviceOrientationTracking() {
            if (!window.DeviceOrientationEvent) {
                return false;
            }
            if (typeof window.DeviceOrientationEvent.requestPermission === `function`) {
                window.DeviceOrientationEvent.requestPermission().then((permission) => {
                    if (permission !== `granted`) {
                        return;
                    }
                    this.enableOffsetDeviceOrientationTracking();
                });
                return true;
            }
            this.enableOffsetDeviceOrientationTracking();
            return true;
        },
        enableOffsetDeviceOrientationTracking() {
            if (this.customOrientationListening) {
                window.removeEventListener(`deviceorientation`, this.boundDeviceOrientationChange);
            }
            this.customOrientationListening = true;
            this.lastDeviceOrientationYaw = null;
            this.customOrientationYawOffset = 0;
            window.addEventListener(`deviceorientation`, this.boundDeviceOrientationChange);
        },
        onDeviceOrientationChange(event) {
            if (!this.viewer || !this.modeAR) {
                return;
            }
            const eulerAngles = getDeviceOrientationEulerAngles(event);
            const devicePitch = eulerAngles[0] * RAD_TO_DEG;
            const roll = -eulerAngles[1] * RAD_TO_DEG;
            const deviceYaw = eulerAngles[2] * RAD_TO_DEG;

            if (this.lastDeviceOrientationYaw == null) {
                this.customOrientationYawOffset = this.viewer.getYaw() + deviceYaw;
            }

            const pitch = devicePitch + this.getPitchOffset();
            const yaw = -deviceYaw + this.customOrientationYawOffset;
            this.lastDeviceOrientationYaw = deviceYaw;
            this.viewer.getConfig().roll = roll;
            this.viewer.setPitch(pitch, false);
            this.viewer.setYaw(yaw, false);
            this.viewerYaw = yaw;
            this.viewerPitch = pitch;
            this.cameraAngle = this.toWorldAngle(yaw);
            this.refreshSideIndicatorsVisibility();
        },
        startDeviceOrientationTracking() {
            if (!this.isDeviceOrientationSupported()) {
                return false;
            }
            if (!this.isMobilePitchEnabled) {
                this.viewer.startOrientation();
                return this.isDeviceOrientationActive();
            }
            return this.startOffsetDeviceOrientationTracking();
        },
        stopOrientation() {
            if (this.customOrientationListening) {
                window.removeEventListener(`deviceorientation`, this.boundDeviceOrientationChange);
            }
            this.customOrientationListening = false;
            this.lastDeviceOrientationYaw = null;
            this.customOrientationYawOffset = 0;
            if (!this.viewer) {
                return;
            }
            this.viewer.stopOrientation();
        },
        resetOrientationPose() {
            if (!this.viewer) {
                return;
            }
            this.viewer.getConfig().roll = 0;
            this.viewer.setHorizonRoll(0);
            this.viewer.setHorizonPitch(0);
            this.viewer.stopMovement();
            this.syncViewStateFromViewer();
        },
        enforceArTargetHfov() {
            if (!this.viewer || !this.modeAR) {
                return;
            }
            this.viewer.setHfovBounds([AR_TARGET_HFOV, AR_TARGET_HFOV]);
            this.viewer.setHfov(AR_TARGET_HFOV, false);
            this.viewerHfov = AR_TARGET_HFOV;
        },
        restoreHfovAfterAr() {
            if (!this.viewer) {
                return;
            }
            if (!this.isMobileDevice) {
                this.viewer.setHfovBounds([WIDEST_HFOV, MOST_ZOOMED_HFOV]);
                this.viewerHfov = this.viewer.getHfov();
                return;
            }
            this.viewer.setHfovBounds([MOBILE_DEFAULT_HFOV, MOBILE_DEFAULT_HFOV]);
            const currentHfov = this.viewer.getHfov();
            if (Math.abs(currentHfov - MOBILE_DEFAULT_HFOV) < HFOV_EPSILON_DEG) {
                this.viewerHfov = currentHfov;
                return;
            }
            this.viewer.setHfov(MOBILE_DEFAULT_HFOV, false);
            this.viewerHfov = MOBILE_DEFAULT_HFOV;
        },
        toggleOrientation() {
            if (!this.viewer) {
                return false;
            }
            if (this.modeAR) {
                const started = this.startDeviceOrientationTracking();
                this.enforceArTargetHfov();
                return started;
            }
            this.stopOrientation();
            this.restoreHfovAfterAr();
            this.resetOrientationPose();
            return false;
        },
        activateArMode(heading = null) {
            if (!this.viewer) {
                return;
            }
            this.isIntroAnimating = false;
            this.cameraAnimationCancelled = true;
            this.cancelArrowHotspotTransition({restoreHotspot: true});
            this.viewer.stopMovement();
            if (heading != null) {
                this.setCameraAngle(heading, {force: true});
            } else {
                requestAnimationFrame(() => {
                    if (!this.viewer || !this.modeAR) {
                        return;
                    }
                    this.$emit(`ar-needs-recalibration`);
                });
            }
            this.toggleOrientation();
            requestAnimationFrame(() => {
                this.enforceArTargetHfov();
                this.updateHotspots({force: true});
            });
            setTimeout(() => {
                this.enforceArTargetHfov();
                this.updateHotspots({force: true});
            }, 80);
            this.refreshSideIndicatorsVisibility();
        },
        deactivateArMode() {
            if (!this.viewer) {
                return;
            }
            this.arPoseSnapshot = null;
            this.toggleOrientation();
            this.centerCameraToDirection({
                duration: CAMERA_RECENTER_DURATION_MS,
                onComplete: () => this.updateHotspots({force: true}),
            });
            this.refreshSideIndicatorsVisibility();
        },
        toggleARMode(heading = null) {
            this.modeAR = !this.modeAR;
            if (this.modeAR) {
                this.activateArMode(heading);
            } else {
                this.deactivateArMode();
            }
            this.$emit(`update:modeAr`, this.modeAR);
        },
        hideTouchAnimation() {
            this.touchAnimationHidden = true;
            if (!this.touchAnimationAlreadyShown) {
                localStorage.setItem(TOUCH_HINT_STORAGE_KEY, `true`);
                this.touchAnimationAlreadyShown = true;
            }
        },
        onUserInteract() {
            this.hideTouchAnimation();
            this.cancelIntroAnimation();
        },
        updateContainerSize() {
            if (!this.container) {
                return;
            }
            this.resize();
        },
        resize() {
            if (!this.viewer) {
                return;
            }
            this.viewer.resize();
            if (!this.isIntroAnimating && !this.arrowTransitionActive && this.viewer.isLoaded()) {
                this.updateHotspots({force: true});
            }
        },
        syncViewStateFromViewer() {
            if (!this.viewer) {
                return;
            }
            this.viewerYaw = Number(this.viewer.getYaw());
            this.viewerPitch = Number(this.viewer.getPitch());
            this.viewerHfov = Number(this.viewer.getHfov());
            this.cameraAngle = this.toWorldAngle(this.viewerYaw);
        },
        setCameraAngle(angle, {force = false} = {}) {
            if (this.isIntroAnimating && !force) {
                return;
            }
            this.cameraAngle = this.normalizeSigned(angle);
            if (!this.viewer) {
                return;
            }

            const nextYaw = this.toViewerYaw(this.cameraAngle);
            const yawDiff = Math.abs(this.normalizeSigned(nextYaw - this.viewerYaw));
            if (!force && yawDiff < CAMERA_YAW_EPSILON_DEG) {
                return;
            }

            this.viewer.setYaw(nextYaw, false);
            this.viewerYaw = nextYaw;
            if (this.customOrientationListening && this.lastDeviceOrientationYaw != null) {
                this.customOrientationYawOffset = nextYaw + this.lastDeviceOrientationYaw;
            }
            this.refreshSideIndicatorsVisibility();
        },
        animateCameraToAngle(targetAngle, duration = INTRO_ANIMATION_DURATION, onComplete = null) {
            if (!this.viewer) {
                return;
            }
            this.cameraAnimationCancelled = false;
            const targetYaw = this.toViewerYaw(targetAngle);
            const animated = this.isMotionAnimationDisabled() ? false : (duration > 0 ? duration : false);

            this.viewer.setYaw(targetYaw, animated, () => {
                this.syncViewStateFromViewer();

                if (onComplete) {
                    onComplete();
                }
            });
        },
        centerCameraToDirection({duration = CAMERA_RECENTER_DURATION_MS, onComplete = null} = {}) {
            if (!this.viewer) {
                return;
            }
            const targetAngle = this.normalizedArrowAngle;
            const targetYaw = this.toViewerYaw(targetAngle);
            const yawDiff = Math.abs(this.normalizeSigned(targetYaw - this.viewerYaw));
            const targetPitch = 0;
            const currentPitch = Number(this.viewer.getPitch());
            const pitchDiff = Math.abs(targetPitch - currentPitch);
            const shouldSkipYawAnimation = yawDiff < CAMERA_YAW_EPSILON_DEG;
            const shouldSkipPitchAnimation = pitchDiff < CAMERA_PITCH_EPSILON_DEG;
            if (shouldSkipYawAnimation && shouldSkipPitchAnimation) {
                this.setCameraAngle(targetAngle, {force: true});
                this.viewer.setPitch(targetPitch, false);
                this.syncViewStateFromViewer();

                if (onComplete) {
                    onComplete();
                }
                return;
            }
            let pendingAnimations = 0;
            const finish = () => {
                pendingAnimations -= 1;
                if (pendingAnimations > 0) {
                    return;
                }
                this.syncViewStateFromViewer();
                this.refreshSideIndicatorsVisibility();

                if (onComplete) {
                    onComplete();
                }
            };

            if (shouldSkipYawAnimation) {
                this.setCameraAngle(targetAngle, {force: true});
            } else {
                pendingAnimations += 1;
                this.viewer.setYaw(targetYaw, duration, finish);
            }

            if (shouldSkipPitchAnimation) {
                this.viewer.setPitch(targetPitch, false);
            } else {
                pendingAnimations += 1;
                this.viewer.setPitch(targetPitch, duration, finish);
            }

            if (pendingAnimations === 0) {
                this.syncViewStateFromViewer();
                this.refreshSideIndicatorsVisibility();

                if (onComplete) {
                    onComplete();
                }
            }
        },
        onSideIndicatorClick() {
            if (this.modeAR) {
                return;
            }
            this.hideTouchAnimation();
            this.centerCameraToDirection({
                duration: CAMERA_RECENTER_DURATION_MS,
                onComplete: () => {
                    this.updateHotspots({force: true});
                },
            });
        },
        onMainHotspotClick(event) {
            event.stopPropagation();
            if (this.isMobilePreview || this.isArrived) {
                return;
            }
            this.$emit(`next-instruction`);
        },
        onContainerClick(event) {
            this.onUserInteract();
            if (this.shouldHandleSideIndicatorTap(event)) {
                this.onSideIndicatorClick();
                return;
            }
            if (!this.shouldHandleMainHotspotTap(event)) {
                return;
            }
            this.onMainHotspotClick(event);
        },
        shouldHandleSideIndicatorTap(event) {
            if (!event || this.modeAR || !this.sideIndicatorsVisible || !this.activeSideIndicator || !this.container) {
                return false;
            }
            const target = this.getSideIndicatorHitTarget();
            if (!target) {
                return false;
            }
            const bounds = this.container.getBoundingClientRect();
            const localX = Number(event.clientX) - bounds.left;
            const localY = Number(event.clientY) - bounds.top;
            if (!Number.isFinite(localX) || !Number.isFinite(localY)) {
                return false;
            }
            const dx = localX - target.x;
            const dy = localY - target.y;
            return (dx * dx + dy * dy) <= target.radius * target.radius;
        },
        getSideIndicatorHitTarget() {
            if (!this.container || !this.activeSideIndicator) {
                return null;
            }
            const width = this.container.offsetWidth;
            const height = this.container.offsetHeight;
            if (!width || !height) {
                return null;
            }
            const centerOffset = SIDE_INDICATOR_EDGE_OFFSET_PX + SIDE_INDICATOR_SIZE_PX / 2;
            return {
                x: this.activeSideIndicator === `left` ? centerOffset : width - centerOffset,
                y: height / 2,
                radius: SIDE_INDICATOR_HIT_RADIUS_PX,
            };
        },
        shouldHandleMainHotspotTap(event) {
            if (!event || this.modeAR || this.isMobilePreview || this.isArrived || !this.viewer || !this.container) {
                return false;
            }
            const hotspotPoint = this.projectToScreen(this.toViewerYaw(this.normalizedArrowAngle), this.getArrowHotspotPitch(), this.arrowProjectScratchB);
            if (!hotspotPoint) {
                return false;
            }
            const bounds = this.container.getBoundingClientRect();
            const localX = Number(event.clientX) - bounds.left;
            const localY = Number(event.clientY) - bounds.top;
            if (!Number.isFinite(localX) || !Number.isFinite(localY)) {
                return false;
            }
            const mainSize = this.getMainArrowCustomSize();
            const discSize = mainSize?.disc ? Number.parseFloat(mainSize.disc) : 90;
            const hitRadius = Math.max(18, discSize * 0.42);
            const dx = localX - hotspotPoint.x;
            const dy = localY - hotspotPoint.y;
            return (dx * dx + dy * dy) <= hitRadius * hitRadius;
        },
        cancelIntroAnimation() {
            if (!this.viewer || this.cameraAnimationCancelled) {
                return;
            }
            if (!this.isIntroAnimating && !this.arrowTransitionActive) {
                return;
            }
            const shouldPlayPopAfterSkip = this.canPlayIntroPopReveal() && (this.isIntroAnimating || this.arrowTransitionActive);
            this.cameraAnimationCancelled = true;
            this.isIntroAnimating = false;
            this.cancelArrowHotspotTransition({restoreHotspot: false});
            this.viewer.stopMovement();
            this.syncViewStateFromViewer();
            this.centerCameraToDirection({
                duration: CAMERA_RECENTER_DURATION_MS,
                onComplete: () => {
                    if (shouldPlayPopAfterSkip) {
                        this.runIntroRevealSequence();
                        return;
                    }
                    this.introAnimationComplete = true;
                    this.updateHotspots({force: true});
                    this.refreshSideIndicatorsVisibility();
                },
            });
        },
        clearIntroRevealSequence() {
            for (const timeoutId of this.introRevealTimeoutIds) {
                clearTimeout(timeoutId);
            }
            this.introRevealTimeoutIds = [];
        },
        queueIntroRevealStep(callback, delayMs) {
            const timeoutId = setTimeout(() => {
                this.introRevealTimeoutIds = this.introRevealTimeoutIds.filter((id) => id !== timeoutId);
                if (this.isDestroyed || !this.viewer) {
                    return;
                }
                callback();
            }, delayMs);
            this.introRevealTimeoutIds?.push(timeoutId);
        },
        runIntroRevealSequence() {
            if (!this.viewer) {
                return;
            }
            this.clearIntroRevealSequence();
            this.introAnimationComplete = false;

            const positions = this.getGuideArrowPositions();
            const totalGuides = positions.length;

            this.updateHotspots({
                force: true,
                showMain: false,
                showGuides: false,
                showSides: false,
            });

            for (let i = 1; i <= totalGuides; i++) {
                this.queueIntroRevealStep(() => {
                    this.updateHotspots({
                        force: true,
                        showMain: false,
                        showGuides: true,
                        guideLimit: i,
                        guidePopIndex: i - 1,
                        showSides: false,
                    });
                }, GUIDE_REVEAL_STEP_MS * (i - 1));
            }

            const afterGuidesDelay = totalGuides > 0
                ? GUIDE_REVEAL_STEP_MS * (totalGuides - 1) + INTRO_MAIN_POP_DELAY_MS
                : 0;

            this.queueIntroRevealStep(() => {
                this.updateHotspots({
                    force: true,
                    showMain: true,
                    showGuides: totalGuides > 0,
                    guideLimit: totalGuides,
                    guidePopIndex: null,
                    popMain: true,
                    showSides: false,
                });
            }, afterGuidesDelay);

            this.queueIntroRevealStep(() => {
                this.introAnimationComplete = true;
                this.updateHotspots({
                    force: true,
                    showMain: true,
                    showGuides: true,
                    guideLimit: totalGuides,
                    showSides: true,
                });
                this.refreshSideIndicatorsVisibility();
            }, afterGuidesDelay + GUIDE_REVEAL_STEP_MS);
        },
        shouldAnimateArrowTransition() {
            if (
                !this.viewer
                || this.gridMode
                || this.isMobilePreview
                || this.isDestroyed
                || !this.animation
                || this.shouldSkipElementAnimations()
                || this.isMotionAnimationDisabled()
            ) {
                return false;
            }
            return true;
        },
        getArrowTurnRotation() {
            const turn = this.normalizeSigned(this.normalizedArrowAngle - this.normalizedFromProlongementAngle);
            return Math.max(-95, Math.min(95, turn));
        },
        cancelArrowHotspotTransition({restoreHotspot = true} = {}) {
            this.clearIntroRevealSequence();
            if (this.arrowTransitionRafId) {
                cancelAnimationFrame(this.arrowTransitionRafId);
                this.arrowTransitionRafId = null;
            }

            const hadTransition = this.arrowTransitionActive || Boolean(this.flyingArrow);
            this.arrowTransitionActive = false;
            this.flyingArrow = null;
            if (hadTransition) {
                this.introAnimationComplete = true;
            }

            if (restoreHotspot && hadTransition && this.viewer) {
                this.updateHotspots({force: true});
            }
        },
        getProjectionState() {
            if (!this.viewer || !this.container) {
                return null;
            }
            const W = this.container.offsetWidth;
            const H = this.container.offsetHeight;
            if (!W || !H) {
                return null;
            }

            const hfovRad = this.viewerHfov * DEG_TO_RAD;
            const tanHalfHfov = Math.tan(hfovRad * 0.5);
            if (Math.abs(tanHalfHfov) < 1e-6) {
                return null;
            }

            const yawRad = this.viewerYaw * DEG_TO_RAD;
            const pitchRad = this.viewerPitch * DEG_TO_RAD;
            const projection = this.projectionStateCache;
            projection.width = W;
            projection.height = H;
            projection.halfWidth = W * 0.5;
            projection.halfHeight = H * 0.5;
            projection.focal = projection.halfWidth / tanHalfHfov;
            projection.vSinYaw = Math.sin(yawRad);
            projection.vCosYaw = Math.cos(yawRad);
            projection.vSinPitch = Math.sin(pitchRad);
            projection.vCosPitch = Math.cos(pitchRad);
            return projection;
        },
        projectToScreen(ptYawDeg, ptPitchDeg, out = {x: 0, y: 0}) {
            const projection = this.getProjectionState();
            if (!projection) {
                return null;
            }

            const pYaw = ptYawDeg * DEG_TO_RAD;
            const pPitch = ptPitchDeg * DEG_TO_RAD;
            const pSinPitch = Math.sin(pPitch);
            const pCosPitch = Math.cos(pPitch);
            const pSinYaw = Math.sin(pYaw);
            const pCosYaw = Math.cos(pYaw);
            const yawDeltaSin = projection.vSinYaw * pCosYaw - projection.vCosYaw * pSinYaw;
            const yawDeltaCos = projection.vCosYaw * pCosYaw + projection.vSinYaw * pSinYaw;
            const visibility = pSinPitch * projection.vSinPitch + pCosPitch * yawDeltaCos * projection.vCosPitch;
            if (visibility <= PATH_DOT_EPSILON) {
                return null;
            }

            const xOffset = -projection.focal * yawDeltaSin * pCosPitch / visibility;
            const yOffset = -projection.focal * (
                pSinPitch * projection.vCosPitch - pCosPitch * yawDeltaCos * projection.vSinPitch
            ) / visibility;
            out.x = projection.halfWidth + xOffset;
            out.y = projection.halfHeight + yOffset;
            return out;
        },
        animateArrowHotspotTransition() {
            if (!this.shouldAnimateArrowTransition()) {
                this.cancelArrowHotspotTransition({restoreHotspot: true});
                return;
            }

            this.cancelArrowHotspotTransition({restoreHotspot: false});
            this.arrowTransitionActive = true;
            this.removeNavigationHotspots();

            const width = this.container.offsetWidth;
            const height = this.container.offsetHeight;
            if (!width || !height) {
                this.cancelArrowHotspotTransition({restoreHotspot: true});
                return;
            }

            const startPoint = {x: width * 0.5, y: height - Math.min(48, height * 0.15)};
            const turnRotation = this.getArrowTurnRotation();
            const turnFactor = Math.min(1, Math.abs(turnRotation) / 60);
            const startTime = performance.now();
            const duration = ARROW_TRANSITION_BASE_DURATION + Math.round(turnFactor * 500);
            const liftPhase = 0.28 + turnFactor * 0.18;
            const totalDuration = duration + ARROW_FADE_OUT_MS;
            const targetYaw = this.toViewerYaw(this.normalizedArrowAngle);
            const midPoint = {
                x: width * 0.5,
                y: startPoint.y - (startPoint.y - height * 0.48) * (0.20 + turnFactor * 0.80),
            };
            const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
            const easeInOutSine = (t) => -(Math.cos(Math.PI * t) - 1) / 2;
            const flying = this.flyingArrowState;
            flying.x = startPoint.x;
            flying.y = startPoint.y;
            flying.scale = 0.72;
            flying.opacity = 0.86;
            flying.rotation = 0;
            this.flyingArrow = flying;

            const draw = (now) => {
                if (this.isDestroyed || !this.viewer) {
                    this.cancelArrowHotspotTransition({restoreHotspot: false});
                    return;
                }

                this.viewerYaw = Number(this.viewer.getYaw());
                this.viewerPitch = Number(this.viewer.getPitch());
                this.viewerHfov = Number(this.viewer.getHfov());

                const elapsed = now - startTime;
                const t = Math.min(1, elapsed / duration);
                let x = midPoint.x;
                let y = midPoint.y;
                let scale = 1;
                let opacity = 1;
                let rotation = turnRotation;

                if (t <= liftPhase) {
                    const phase = easeOutCubic(t / liftPhase);
                    x = startPoint.x + (midPoint.x - startPoint.x) * phase;
                    y = startPoint.y + (midPoint.y - startPoint.y) * phase;
                    rotation = turnRotation * phase;
                    scale = 0.72 + 0.30 * phase;
                    opacity = 0.86 + 0.14 * phase;
                } else {
                    const phase = easeInOutSine((t - liftPhase) / (1 - liftPhase));
                    let landingPoint = this.projectToScreen(targetYaw, this.getArrowHotspotPitch(), this.arrowProjectScratchA);
                    if (!landingPoint) {
                        landingPoint = midPoint;
                    }
                    x = midPoint.x + (landingPoint.x - midPoint.x) * phase;
                    y = midPoint.y + (landingPoint.y - midPoint.y) * phase;
                    rotation = turnRotation * (1 - phase);
                    scale = 1.02 - 0.02 * phase;
                }

                if (elapsed >= duration) {
                    let landingPoint = this.projectToScreen(targetYaw, this.getArrowHotspotPitch(), this.arrowProjectScratchA);
                    if (!landingPoint) {
                        landingPoint = midPoint;
                    }
                    const fadeProgress = Math.min(1, (elapsed - duration) / ARROW_FADE_OUT_MS);
                    x = landingPoint.x;
                    y = landingPoint.y;
                    rotation = 0;
                    scale = 1 - 0.08 * fadeProgress;
                    opacity = 1 - fadeProgress;

                    if (fadeProgress >= 1) {
                        this.arrowTransitionActive = false;
                        this.flyingArrow = null;
                        this.runIntroRevealSequence();
                        return;
                    }
                }

                flying.x = x;
                flying.y = y;
                flying.scale = scale;
                flying.opacity = opacity;
                flying.rotation = rotation;

                if (elapsed >= totalDuration) {
                    this.arrowTransitionActive = false;
                    this.flyingArrow = null;
                    this.runIntroRevealSequence();
                    return;
                }

                this.arrowTransitionRafId = requestAnimationFrame(draw);
            };

            this.arrowTransitionRafId = requestAnimationFrame(draw);
        },
        startSideIndicatorVisibilityTracking() {
            this.stopSideIndicatorVisibilityTracking();
            if (!this.viewer || this.isMobilePreview || this.isDestroyed) {
                return;
            }
            this.sideIndicatorRefreshIntervalId = window.setInterval(() => {
                if (!this.viewer || this.isDestroyed) {
                    this.stopSideIndicatorVisibilityTracking();
                    return;
                }
                this.refreshSideIndicatorsVisibility();
            }, SIDE_INDICATOR_REFRESH_MS);
        },
        stopSideIndicatorVisibilityTracking() {
            if (this.sideIndicatorRefreshIntervalId) {
                clearInterval(this.sideIndicatorRefreshIntervalId);
                this.sideIndicatorRefreshIntervalId = null;
            }
        },
        setSideIndicatorsVisible(visible) {
            const nextValue = Boolean(visible);
            if (this.sideIndicatorsVisible === nextValue) {
                return;
            }
            this.sideIndicatorsVisible = nextValue;
        },
        setActiveSideIndicator(side) {
            const nextSide = side === `left` || side === `right` ? side : null;
            if (this.activeSideIndicator === nextSide) {
                return;
            }
            this.activeSideIndicator = nextSide;
        },
        shouldShowSideIndicators() {
            if (!this.viewer || this.isMobilePreview || this.isArrived) {
                return false;
            }
            if (this.isIntroAnimating || this.arrowTransitionActive || !this.introAnimationComplete) {
                return false;
            }
            return true;
        },
        getSideIndicatorForCurrentHeading() {
            const targetYaw = this.toViewerYaw(this.normalizedArrowAngle);
            const deltaToTarget = this.normalizeSigned(targetYaw - this.viewerYaw);
            if (Math.abs(deltaToTarget) < CAMERA_YAW_EPSILON_DEG) {
                return null;
            }
            // Show indicator on the same side as the nearest turn direction.
            return deltaToTarget < 0 ? `left` : `right`;
        },
        isMainArrowVisibleInViewport() {
            if (!this.viewer || !this.container) {
                return false;
            }
            const projectionPoint = this.projectToScreen(this.toViewerYaw(this.normalizedArrowAngle), this.getArrowHotspotPitch(), this.arrowProjectScratchB);
            if (!projectionPoint) {
                return false;
            }
            const width = this.container.offsetWidth;
            const height = this.container.offsetHeight;
            if (!width || !height) {
                return false;
            }
            return projectionPoint.x >= -SIDE_INDICATOR_SCREEN_MARGIN_PX
                && projectionPoint.x <= width + SIDE_INDICATOR_SCREEN_MARGIN_PX
                && projectionPoint.y >= -SIDE_INDICATOR_SCREEN_MARGIN_PX
                && projectionPoint.y <= height + SIDE_INDICATOR_SCREEN_MARGIN_PX;
        },
        refreshSideIndicatorsVisibility() {
            if (!this.shouldShowSideIndicators()) {
                this.setSideIndicatorsVisible(false);
                this.setActiveSideIndicator(null);
                return;
            }
            this.viewerYaw = Number(this.viewer.getYaw());
            this.viewerPitch = Number(this.viewer.getPitch());
            this.viewerHfov = Number(this.viewer.getHfov());
            if (this.isMainArrowVisibleInViewport()) {
                this.setSideIndicatorsVisible(false);
                this.setActiveSideIndicator(null);
                return;
            }
            this.setActiveSideIndicator(this.getSideIndicatorForCurrentHeading());
            this.setSideIndicatorsVisible(Boolean(this.activeSideIndicator));
        },
        onImgAngleChanged() {
            if (!this.viewer) {
                return;
            }
            const currentViewerYaw = Number(this.viewer.getYaw());
            this.setCameraAngle(this.toWorldAngle(currentViewerYaw));
            this.updateHotspots();
            this.refreshSideIndicatorsVisibility();
        },
        removeHotspot(id) {
            if (!this.viewer) {
                return;
            }
            try {
                this.viewer.removeHotSpot(id);
            } catch (err) {
                // Ignore if the hotspot does not exist yet.
            }
        },
        removeNavigationHotspots() {
            this.removeHotspot(NAV_HOTSPOT_ID);
            this.removeHotspot(FROM_NAV_HOTSPOT_ID);
            this.removeGuideArrowHotspots();
            this.removeSideArrowHotspots();
        },
        getArrowHotspotPitch() {
            const basePitch = this.isMobilePreview
                ? (this.isArrived ? -10 : -22)
                : (this.isArrived ? -12 : -20);
            let pitch = basePitch;
            const aspectRatio = this.getContainerAspectRatio();
            const isTallMobileArView = !this.preview && this.isMobileDevice && aspectRatio < 0.82;
            if (isTallMobileArView) {
                // Mobile AR (non-split) needs hotspots lower on Y than split mode.
                pitch -= 4;
            }
            return this.clampPitchToViewport(pitch, {margin: this.fullscreen ? 6 : 4});
        },
        getContainerAspectRatio() {
            const width = this.container.offsetWidth;
            const height = this.container.offsetHeight;
            if (!width || !height) {
                return 1;
            }
            return width / height;
        },
        getVerticalFovDegrees(hfov = this.viewerHfov) {
            const resolvedHfov = Number(hfov);
            if (resolvedHfov <= 0) {
                return 60;
            }
            const aspectRatio = Math.max(0.2, this.getContainerAspectRatio());
            const halfHfovRad = (resolvedHfov * DEG_TO_RAD) * 0.5;
            const halfVfovRad = Math.atan(Math.tan(halfHfovRad) / aspectRatio);
            const verticalFov = halfVfovRad * 2 * RAD_TO_DEG;
            if (verticalFov <= 0) {
                return 60;
            }
            return Math.min(170, verticalFov);
        },
        clampPitchToViewport(pitch, {margin = 4} = {}) {
            const verticalFov = this.getVerticalFovDegrees();
            const halfVfov = Math.max(8, verticalFov * 0.5);
            const maxDown = Math.max(3, halfVfov - Math.max(0, margin));
            // Anchor clamp to horizon so AR scene switches don't push markers too high.
            const centerPitch = 0;
            const minPitch = centerPitch - maxDown;
            return Math.max(Number(pitch), minPitch);
        },
        colorWithAlpha(color, alpha) {
            if (!color) {
                return `rgba(34, 151, 255, ${alpha})`;
            }
            if (color.startsWith(`#`)) {
                let hex = color.slice(1);
                if (hex.length === 3) {
                    hex = hex.split(``).map((char) => `${char}${char}`).join(``);
                }
                if (hex.length === 6) {
                    const r = parseInt(hex.slice(0, 2), 16);
                    const g = parseInt(hex.slice(2, 4), 16);
                    const b = parseInt(hex.slice(4, 6), 16);
                    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
                }
            }
            return color;
        },
        applyArrowHotspotVisuals(hotSpotDiv) {
            if (!hotSpotDiv) {
                return;
            }
            const isFromHotspot = hotSpotDiv.classList.contains(`pano-custom-hotspot-from`);
            const markerColor = isFromHotspot ? BACK_MARKER_COLOR : this.arrowColor;
            hotSpotDiv.style.setProperty(`--pano-arrow-color`, markerColor);

            const disc = hotSpotDiv.querySelector(`.pano-custom-hotspot-disc`);
            if (!disc) {
                return;
            }
            if (isFromHotspot) {
                disc.style.background = `radial-gradient(circle at 50% 68%, ${this.colorWithAlpha(markerColor, 1)} 0%, ${this.colorWithAlpha(markerColor, 0.85)} 50%, ${this.colorWithAlpha(markerColor, 0.55)} 80%, ${this.colorWithAlpha(markerColor, 0.4)} 100%)`;
                disc.style.border = `2px solid rgba(255, 255, 255, 0.9)`;
                disc.style.boxShadow = `0 -10px 18px 4px ${this.colorWithAlpha(markerColor, 0.22)}`;
                return;
            }
            disc.style.background = `radial-gradient(circle, ${this.colorWithAlpha(markerColor, 1)} 0%, ${this.colorWithAlpha(markerColor, 0.85)} 50%, ${this.colorWithAlpha(markerColor, 0.55)} 80%, ${this.colorWithAlpha(markerColor, 0.4)} 100%)`;
            disc.style.border = `2px solid rgba(255, 255, 255, 0.9)`;
            disc.style.boxShadow = `0 0 14px 2px ${this.colorWithAlpha(markerColor, 0.22)}, 0 6px 7px 1px rgba(0, 0, 0, 0.38)`;
        },
        composeTransformWithScale(baseTransform, scale) {
            const transformBase = (baseTransform && baseTransform !== `none`) ? baseTransform : ``;
            return `${transformBase}${transformBase ? ` ` : ``}scale(${scale})`;
        },
        animateSpringReveal(target, {
            baseTransform = `none`,
            fromScale = 0.66,
            toScale = 1,
            fromOpacity = 0,
            maxDurationMs = INTRO_REVEAL_ANIM_MS,
            mass = INTRO_REVEAL_SPRING.mass,
            damping = INTRO_REVEAL_SPRING.damping,
            stiffness = INTRO_REVEAL_SPRING.stiffness,
        } = {}) {
            if (!target) {
                return;
            }

            if (target.__introPopRafId) {
                cancelAnimationFrame(target.__introPopRafId);
                target.__introPopRafId = null;
            }

            let scale = fromScale;
            let velocity = 0;
            let lastTime = performance.now();
            const startTime = lastTime;
            target.style.opacity = `${fromOpacity}`;
            target.style.transform = this.composeTransformWithScale(baseTransform, scale);
            target.style.filter = `blur(2px)`;
            target.style.willChange = `transform, opacity, filter`;

            const settleAndStop = () => {
                if (target.__introPopRafId) {
                    cancelAnimationFrame(target.__introPopRafId);
                    target.__introPopRafId = null;
                }
                target.style.opacity = `1`;
                target.style.transform = this.composeTransformWithScale(baseTransform, toScale);
                target.style.filter = `none`;
                target.style.willChange = ``;
            };

            const tick = (now) => {
                if (!target.isConnected) {
                    settleAndStop();
                    return;
                }

                const dt = Math.max(0.001, Math.min(0.034, (now - lastTime) / 1000));
                lastTime = now;

                const displacement = scale - toScale;
                const springForce = -stiffness * displacement;
                const dampingForce = -damping * velocity;
                const acceleration = (springForce + dampingForce) / Math.max(0.001, mass);
                velocity += acceleration * dt;
                scale += velocity * dt;

                const elapsedMs = now - startTime;
                const opacityProgress = Math.min(1, elapsedMs / 200);
                const opacity = fromOpacity + (1 - fromOpacity) * opacityProgress;
                const blurPx = Math.max(0, 2 * (1 - opacityProgress));
                target.style.opacity = `${opacity}`;
                target.style.transform = this.composeTransformWithScale(baseTransform, scale);
                target.style.filter = blurPx > 0.01 ? `blur(${blurPx.toFixed(2)}px)` : `none`;

                const settled = Math.abs(scale - toScale) < 0.0015 && Math.abs(velocity) < 0.004;
                if (settled || elapsedMs >= maxDurationMs) {
                    settleAndStop();
                    return;
                }

                target.__introPopRafId = requestAnimationFrame(tick);
            };

            target.__introPopRafId = requestAnimationFrame(tick);
        },
        applyPopRevealAnimation(target, {baseTransform = `none`} = {}) {
            if (!target) {
                return;
            }
            this.animateSpringReveal(target, {
                baseTransform,
                fromScale: 0.66,
                toScale: 1,
                fromOpacity: 0,
                mass: INTRO_REVEAL_SPRING.mass,
                damping: INTRO_REVEAL_SPRING.damping,
                stiffness: INTRO_REVEAL_SPRING.stiffness,
            });
        },
        buildArrowHotspotDOM(hotSpotDiv, {flipped = false, useLocationIcon = false, hideGlyph = false, customSize = null, perspectiveTilt = 50, pop = false} = {}) {
            hotSpotDiv.innerHTML = ``;
            if (customSize && customSize.wrapper) {
                hotSpotDiv.style.width = customSize.wrapper;
                hotSpotDiv.style.height = customSize.wrapper;
            } else {
                hotSpotDiv.style.width = `100px`;
                hotSpotDiv.style.height = `100px`;
            }

            const wrapper = document.createElement(`div`);
            wrapper.className = `pano-custom-hotspot-wrapper`;
            if (customSize && customSize.wrapper) {
                wrapper.style.width = customSize.wrapper;
                wrapper.style.height = customSize.wrapper;
            }

            const disc = document.createElement(`div`);
            disc.className = `pano-custom-hotspot-disc`;
            if (customSize && customSize.disc) {
                disc.style.width = customSize.disc;
                disc.style.height = customSize.disc;
            }
            disc.style.transform = `rotateX(${perspectiveTilt}deg)`;

            let arrowGlyph = null;
            if (hideGlyph) {
                arrowGlyph = null;
            } else if (useLocationIcon) {
                // Render the original location.svg inline with blue fill + white stroke
                arrowGlyph = document.createElementNS(`http://www.w3.org/2000/svg`, `svg`);
                arrowGlyph.setAttribute(`viewBox`, `-1 -1 26 26`);
                arrowGlyph.setAttribute(`class`, `pano-custom-hotspot-location-icon`);
                arrowGlyph.style.width = `72px`;
                arrowGlyph.style.height = `72px`;
                const path = document.createElementNS(`http://www.w3.org/2000/svg`, `path`);
                path.setAttribute(`d`, `M10.115 21.811c.606.5 1.238.957 1.885 1.403a27 27 0 0 0 1.885-1.403a28 28 0 0 0 2.853-2.699C18.782 16.877 21 13.637 21 10a9 9 0 1 0-18 0c0 3.637 2.218 6.876 4.262 9.112a28 28 0 0 0 2.853 2.7M12 13.25a3.25 3.25 0 1 1 0-6.5a3.25 3.25 0 0 1 0 6.5`);
                path.setAttribute(`fill`, `#d97575`);
                path.setAttribute(`stroke`, `#fff`);
                path.setAttribute(`stroke-width`, `1`);
                path.setAttribute(`stroke-linejoin`, `round`);
                arrowGlyph.appendChild(path);
                let locationSize = null;
                let wrapperTranslateY = -35;
                if (customSize && customSize.disc) {
                    locationSize = Math.max(30, Math.round(parseFloat(customSize.disc) * 0.46));
                    arrowGlyph.style.width = `${locationSize}px`;
                    arrowGlyph.style.height = `${locationSize}px`;
                    wrapperTranslateY = -Math.round(locationSize * 0.82);
                }
                // Simple 2D transform anchored to wrapper centre — no 3D needed.
                const locationBaseTransform = `translate(-50%, -50%) translateY(${wrapperTranslateY}px)`;
                arrowGlyph.style.setProperty(`--pano-location-transform`, locationBaseTransform);
            } else {
                arrowGlyph = document.createElement(`img`);
                arrowGlyph.src = this.arrowHeadSrc;
                arrowGlyph.className = `pano-custom-hotspot-arrow-head`;
                arrowGlyph.alt = ``;
                arrowGlyph.draggable = false;
                if (customSize && customSize.chevron) {
                    arrowGlyph.style.width = customSize.chevron;
                }
                if (flipped) {
                    arrowGlyph.classList.add(`is-flipped`);
                }
            }
            if (arrowGlyph) {
                if (useLocationIcon) {
                    // Appended to wrapper (above disc) so it is not clipped
                    // by the disc's 3D transform context.
                    wrapper.appendChild(disc);
                    wrapper.appendChild(arrowGlyph);
                } else {
                    disc.appendChild(arrowGlyph);
                    wrapper.appendChild(disc);
                }
            } else {
                wrapper.appendChild(disc);
            }
            hotSpotDiv.appendChild(wrapper);

            this.applyArrowHotspotVisuals(hotSpotDiv);
            if (pop) {
                this.applyPopRevealAnimation(disc, {baseTransform: `rotateX(${perspectiveTilt}deg)`});
                if (useLocationIcon) {
                    const locationBaseTransform = arrowGlyph.style.getPropertyValue(`--pano-location-transform`);
                    this.applyPopRevealAnimation(arrowGlyph, {baseTransform: locationBaseTransform});
                }
            }
        },
        buildGridDOM(hotSpotDiv, {pop = false} = {}) {
            hotSpotDiv.innerHTML = ``;
            hotSpotDiv.style.width = `96px`;
            hotSpotDiv.style.height = `96px`;

            const wrapper = document.createElement(`div`);
            wrapper.className = `pano-grid-only-wrapper`;

            const marker = document.createElement(`div`);
            marker.className = `pano-grid-only-marker`;

            const ring = document.createElement(`div`);
            ring.className = `pano-grid-only-ring`;

            const icon = document.createElement(`i`);
            icon.className = [
                this.fontAwesomePrefix || `fa-solid`,
                this.gridModeGlyph || `fa-location-crosshairs`,
                `pano-grid-only-icon`,
            ].join(` `);

            marker.appendChild(ring);
            marker.appendChild(icon);
            wrapper.appendChild(marker);
            hotSpotDiv.appendChild(wrapper);

            const accentColor = this.arrowColor;
            marker.style.setProperty(`--grid-only-color`, accentColor);
            marker.style.setProperty(`--grid-only-color-soft`, this.colorWithAlpha(accentColor, 0.18));
            marker.style.setProperty(`--grid-only-color-glow`, this.colorWithAlpha(accentColor, 0.42));

            if (pop) {
                this.applyPopRevealAnimation(marker);
            }
        },
        getArrowHotspotDisplayConfig() {
            const isMobileRuntime = this.isMobileDevice;
            const isSmallPreview = this.isMobilePreview && isMobileRuntime;
            const isTinyPreview = isSmallPreview && this.container.offsetWidth <= 120;
            return {isSmallPreview, isTinyPreview};
        },
        getCustomSizeFromScale(scale) {
            return {
                wrapper: `${Math.round(100 * scale)}px`,
                disc: `${Math.round(90 * scale)}px`,
                chevron: `${Math.round(28 * scale)}px`,
                border: `${Math.max(5, Math.round(8 * scale))}px`,
            };
        },
        getMainArrowCustomSize() {
            if (!this.isMobilePreview) {
                return null;
            }
            const display = this.getArrowHotspotDisplayConfig();
            const scale = display.isTinyPreview ? 0.5 : display.isSmallPreview ? 0.58 : 0.66;
            return this.getCustomSizeFromScale(scale);
        },
        getGuideArrowPositions() {
            const showBackMarkers = !this.isFirstStep && !this.isArrived;
            const behind = this.normalizeSigned(this.normalizedFromHotspotAngle);
            const forward = this.normalizedArrowAngle;
            const arrowPitch = this.getArrowHotspotPitch();
            const height = this.container.offsetHeight;
            const width = this.container.offsetWidth;
            if (!height || !width) {
                return [];
            }
            const bounds = this.container.getBoundingClientRect();
            const stepRatio = this.isMobileDevice ? GUIDE_PITCH_STEP_HEIGHT_RATIO_MOBILE : GUIDE_PITCH_STEP_HEIGHT_RATIO_DESKTOP;
            const stepPx = height * stepRatio;
            const centerX = bounds.left + width * 0.5;
            const centerY = bounds.top + height * 0.5;
            const centerCoords = this.viewer.mouseEventToCoords({
                clientX: centerX,
                clientY: centerY,
            });
            const stepCoords = this.viewer.mouseEventToCoords({
                clientX: centerX,
                clientY: centerY - stepPx,
            });
            if (!Array.isArray(centerCoords) || !Array.isArray(stepCoords)) {
                return [];
            }
            const guideStep = Math.abs(Number(stepCoords[0]) - Number(centerCoords[0]));
            const centerPitch = this.modeAR ? Number(this.viewerPitch) : 0;
            const pitchGap = Math.abs(arrowPitch - centerPitch);
            const estimatedDotCount = guideStep > 0 ? Math.round(pitchGap / guideStep) : 5;
            const dotCount = Math.max(3, Math.min(4, estimatedDotCount || 5));
            if (dotCount <= 0) {
                return [];
            }
            const yaw = this.toViewerYaw(forward);
            const backYaw = this.toViewerYaw(behind);
            const footPitch = MIN_PITCH_DEG;
            const pitchSpan = Math.max(0.001, arrowPitch - footPitch);
            const startInsetRatio = 0.2;
            const endInsetRatio = 0.1;
            const usablePathRatio = Math.max(0.1, 1 - startInsetRatio - endInsetRatio);

            const forwardPositions = [];
            for (let i = 1; i <= dotCount; i++) {
                const sequenceProgress = i / (dotCount + 1);
                const progressToArrow = startInsetRatio + usablePathRatio * sequenceProgress;
                forwardPositions.push({
                    yaw,
                    pitch: footPitch + pitchSpan * progressToArrow,
                    sizeScale: 0.5 + sequenceProgress * 0.4,
                    tiltDeg: 32 + sequenceProgress * 20,
                    isBack: false,
                });
            }
            const backPositions = [];
            if (showBackMarkers) {
                for (let i = 1; i <= dotCount; i++) {
                    const sequenceProgress = i / (dotCount + 1);
                    const progressToArrow = startInsetRatio + usablePathRatio * sequenceProgress;
                    backPositions.push({
                        yaw: backYaw,
                        pitch: footPitch + pitchSpan * progressToArrow,
                        sizeScale: 0.5 + sequenceProgress * 0.4,
                        tiltDeg: 32 + sequenceProgress * 20,
                        isBack: true,
                    });
                }
            }
            return backPositions.concat(forwardPositions);
        },
        removeGuideArrowHotspots() {
            if (!this.viewer) {
                this.guideHotspotIds = [];
                return;
            }
            for (const id of this.guideHotspotIds) {
                this.removeHotspot(id);
            }
            this.guideHotspotIds = [];
        },
        updateGuideArrowHotspots({limit = null, popIndex = null} = {}) {
            if (!this.viewer || this.isMobilePreview || this.isArrived) {
                this.removeGuideArrowHotspots();
                return;
            }

            this.removeGuideArrowHotspots();
            const display = this.getArrowHotspotDisplayConfig();
            const baseScale = display.isTinyPreview ? 0.32 : display.isSmallPreview ? 0.42 : 1;
            const positions = this.getGuideArrowPositions();
            const ids = [];
            const maxCount = limit === null ? positions.length : Math.max(0, Math.min(limit, positions.length));

            for (let i = 0; i < maxCount; i++) {
                const pos = positions[i];
                const id = `${GUIDE_HOTSPOT_ID_PREFIX}${i}`;
                const customSize = this.getCustomSizeFromScale(baseScale * pos.sizeScale);
                ids.push(id);

                this.viewer.addHotSpot({
                    id,
                    type: `info`,
                    yaw: pos.yaw,
                    pitch: pos.pitch,
                    cssClass: `pano-custom-hotspot pano-custom-hotspot-guide`,
                    createTooltipFunc: (hotSpotDiv) => this.buildGuideDotDOM(hotSpotDiv, {
                        customSize,
                        tiltDeg: pos.tiltDeg,
                        isBack: pos.isBack,
                        fadeIn: popIndex === i,
                    }),
                });
            }

            this.guideHotspotIds = ids;
        },
        buildGuideDotDOM(hotSpotDiv, {customSize = null, tiltDeg = 50, isBack = false, fadeIn = false} = {}) {
            hotSpotDiv.innerHTML = ``;

            const wrapper = document.createElement(`div`);
            wrapper.className = `pano-custom-hotspot-wrapper`;
            // Keep full hotspot size so the guide marker stays perfectly centered on yaw/pitch.
            wrapper.style.width = `100px`;
            wrapper.style.height = `100px`;

            const dot = document.createElement(`div`);
            dot.className = `pano-custom-hotspot-guide-dot`;
            const dotSize = customSize && customSize.disc ? `${Math.max(16, Math.round(parseFloat(customSize.disc) * 0.36))}px` : `22px`;
            const color = isBack ? BACK_MARKER_COLOR : this.arrowColor;
            dot.style.width = dotSize;
            dot.style.height = dotSize;
            dot.style.borderRadius = `50%`;
            dot.style.background = `radial-gradient(circle, ${this.colorWithAlpha(color, 1)} 0%, ${this.colorWithAlpha(color, 0.82)} 65%, ${this.colorWithAlpha(color, 0.42)} 100%)`;
            dot.style.border = `2px solid rgba(255, 255, 255, 0.95)`;
            dot.style.boxShadow = `0 0 7px 1px ${this.colorWithAlpha(color, 0.32)}, 0 3px 4px 0 rgba(0, 0, 0, 0.35)`;
            // Use the same tilt convention as hotspot arrows so dots are slightly stretched in perspective.
            dot.style.transform = `rotateX(${tiltDeg}deg)`;
            dot.style.transformStyle = `preserve-3d`;
            if (fadeIn) {
                this.applyPopRevealAnimation(dot, {baseTransform: `rotateX(${tiltDeg}deg)`});
            }

            wrapper.appendChild(dot);
            hotSpotDiv.appendChild(wrapper);
        },
        buildSideArrowDOM(hotSpotDiv, pointLeft) {
            hotSpotDiv.innerHTML = ``;

            const wrapper = document.createElement(`div`);
            wrapper.className = `pano-custom-hotspot-wrapper`;
            wrapper.style.width = `100px`;
            wrapper.style.height = `100px`;

            const disc = document.createElement(`div`);
            disc.className = `pano-custom-hotspot-disc`;
            disc.style.width = `90px`;
            disc.style.height = `90px`;
            disc.style.transform = `rotateX(0deg)`;
            disc.style.position = `relative`;

            const color = this.arrowColor;
            disc.style.background = `radial-gradient(circle, ${this.colorWithAlpha(color, 0.85)} 0%, ${this.colorWithAlpha(color, 0.65)} 50%, ${this.colorWithAlpha(color, 0.35)} 80%, ${this.colorWithAlpha(color, 0)} 100%)`;
            disc.style.boxShadow = `0 0 18px 4px ${this.colorWithAlpha(color, 0.5)}`;

            const buildChevron = (offsetX) => {
                const chevron = document.createElement(`div`);
                chevron.className = `pano-custom-hotspot-chevron`;
                chevron.style.width = `22px`;
                chevron.style.height = `22px`;
                chevron.style.borderTopWidth = `7px`;
                chevron.style.borderRightWidth = `7px`;
                chevron.style.position = `absolute`;
                chevron.style.left = `50%`;
                chevron.style.top = `50%`;
                chevron.style.transform = `translate(calc(-50% + ${offsetX}px), -50%) ${pointLeft ? `rotate(-135deg)` : `rotate(45deg)`}`;
                chevron.style.filter = `drop-shadow(0 1px 3px rgba(0, 0, 0, 0.35))`;
                return chevron;
            };

            const leadOffset = pointLeft ? -6 : 6;
            const trailOffset = pointLeft ? 14 : -14;
            disc.appendChild(buildChevron(trailOffset));
            disc.appendChild(buildChevron(leadOffset));

            wrapper.appendChild(disc);
            hotSpotDiv.appendChild(wrapper);
        },
        removeSideArrowHotspots() {
            if (this.viewer) {
                // Keep removing legacy hotspot IDs for compatibility with older viewer state.
                this.removeHotspot(SIDE_HOTSPOT_LEFT_ID);
                this.removeHotspot(SIDE_HOTSPOT_RIGHT_ID);
            }
            this.sideHotspotLeftExists = false;
            this.sideHotspotRightExists = false;
            this.setSideIndicatorsVisible(false);
            this.setActiveSideIndicator(null);
        },
        updateSideArrowHotspots() {
            this.removeSideArrowHotspots();
            this.refreshSideIndicatorsVisibility();
        },
        getHotspotCssClass() {
            return [
                `pano-custom-hotspot`,
                this.isArrived ? `pano-custom-hotspot-arrived` : ``,
            ].filter(Boolean).join(` `);
        },
        updateHotspots({
            force = false,
            showMain = true,
            showFrom = true,
            showGuides = true,
            showSides = true,
            guideLimit = null,
            guidePopIndex = null,
            popMain = false,
        } = {}) {
            if (!this.viewer || !this.viewer.isLoaded()) {
                return;
            }
            this.syncViewStateFromViewer();
            if ((this.isIntroAnimating || this.arrowTransitionActive) && !force) {
                return;
            }

            this.removeNavigationHotspots();

            if (this.imageOnly) {
                this.setSideIndicatorsVisible(false);
                return;
            }

            if (this.gridMode) {
                this.addSimpleHotSpot();
                this.refreshSideIndicatorsVisibility();
                return;
            }

            const pitch = this.getArrowHotspotPitch();
            const cssClass = this.getHotspotCssClass();
            const isPreviewMode = this.isMobilePreview;
            const hideBackMarkers = this.isFirstStep || this.isArrived;
            const effectiveShowFrom = isPreviewMode || hideBackMarkers ? false : showFrom;
            const effectiveShowGuides = isPreviewMode ? false : showGuides;
            const effectiveShowSides = isPreviewMode ? false : showSides;
            const mainCustomSize = this.getMainArrowCustomSize();
            const fromCustomSize = this.getCustomSizeFromScale(0.7);

            if (showMain && this.viewer) {
                this.viewer.addHotSpot({
                    id: NAV_HOTSPOT_ID,
                    type: `info`,
                    yaw: this.toViewerYaw(this.normalizedArrowAngle),
                    pitch,
                    cssClass: `${cssClass} pano-custom-hotspot-main pano-custom-hotspot-main-pulse`,
                    createTooltipFunc: (hotSpotDiv) => this.buildArrowHotspotDOM(hotSpotDiv, {
                        flipped: false,
                        useLocationIcon: this.isArrived,
                        customSize: mainCustomSize,
                        pop: popMain,
                    }),
                });
            }
            if (effectiveShowFrom) {
                this.viewer.addHotSpot({
                    id: FROM_NAV_HOTSPOT_ID,
                    type: `info`,
                    yaw: this.toViewerYaw(this.normalizeSigned(this.normalizedFromHotspotAngle)),
                    pitch,
                    cssClass: `${cssClass} pano-custom-hotspot-from`,
                    createTooltipFunc: (hotSpotDiv) => this.buildArrowHotspotDOM(hotSpotDiv, {
                        hideGlyph: true,
                        customSize: fromCustomSize,
                    }),
                });
            }

            if (effectiveShowGuides) {
                this.updateGuideArrowHotspots({limit: guideLimit, popIndex: guidePopIndex});
            } else {
                this.removeGuideArrowHotspots();
            }

            if (effectiveShowSides) {
                this.updateSideArrowHotspots();
            } else {
                this.setSideIndicatorsVisible(false);
            }
            this.refreshSideIndicatorsVisibility();
        },
        addSimpleHotSpot({pop = false} = {}) {
            if (!this.viewer || !this.viewer.isLoaded()) {
                return;
            }
            this.viewer.addHotSpot({
                id: NAV_HOTSPOT_ID,
                pitch: this.getArrowHotspotPitch(),
                yaw: this.toViewerYaw(this.normalizedArrowAngle),
                type: `info`,
                cssClass: `pano-grid-only`,
                createTooltipFunc: (hotSpotDiv) => this.buildGridDOM(hotSpotDiv, {pop}),
            })
        }
    },
    beforeDestroy() {
        this.isDestroyed = true;
        this.isPanoLoading = false;
        this.clearIntroRevealSequence();
        this.cancelArrowHotspotTransition({restoreHotspot: false});

        if (this.boundWindowResize) {
            window.removeEventListener(`resize`, this.boundWindowResize);
            this.boundWindowResize = null;
        }

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }

        this.destroyViewer();
    },
    watch: {
        panoramaSrc() {
            this.isPanoLoading = true;
            this.initializeViewer({
                animateIntro: !this.isMobilePreview,
                preserveArPose: false,
            });
        },
        isArrived() {
            this.updateHotspots();
        },
        imageOnly() {
            this.updateHotspots({force: true});
        },
        normalizedFromProlongementAngle(newAngle) {
            if (this.modeAR) {
                this.updateHotspots({force: true});
                return;
            }
            if (this.isPanoLoading || this.isIntroAnimating) {
                return;
            }
            const shouldLockToDestination = this.isMobilePreview || this.isFirstStep;
            const targetAngle = shouldLockToDestination ? this.normalizedArrowAngle : this.normalizeSigned(newAngle);
            this.setCameraAngle(targetAngle, {force: shouldLockToDestination});
            if (!this.isIntroAnimating) {
                this.updateHotspots();
            }
        },
        imgAngle() {
            this.onImgAngleChanged();
        },
        normalizedArrowAngle(newAngle, oldAngle) {
            if (newAngle === oldAngle) {
                return;
            }
            if (this.modeAR) {
                this.updateHotspots({force: true});
                return;
            }
            if (this.isPanoLoading || this.isIntroAnimating) {
                return;
            }
            if (this.isMobilePreview) {
                this.setCameraAngle(this.normalizedArrowAngle, {force: true});
                this.updateHotspots({
                    force: true,
                    showMain: true,
                    showGuides: false,
                    showSides: false,
                });
                return;
            }
            if (this.isFirstStep) {
                this.setCameraAngle(this.normalizedArrowAngle, {force: true});
                this.updateHotspots({force: true});
                return;
            }
            if (!this.isIntroAnimating) {
                this.updateHotspots();
            }
            const isSmallTurn = this.getTurnDeltaDegrees(this.normalizedFromProlongementAngle, newAngle) < MIN_INTRO_ANIMATION_TURN_DEG;
            if (isSmallTurn) {
                this.setCameraAngle(this.normalizedArrowAngle, {force: true});
                if (this.shouldTriggerPopOnSmallTurn(this.normalizedFromProlongementAngle, newAngle)) {
                    this.runIntroRevealSequence();
                } else {
                    this.updateHotspots({force: true});
                }
                return;
            }
            this.animateCameraToAngle(this.normalizedArrowAngle, INTRO_ANIMATION_DURATION, () => {
                if (!this.isIntroAnimating) {
                    this.updateHotspots();
                }
            });
        },
        arrowColor() {
            this.updateHotspots();
        },
        preview() {
            this.initializeViewer({animateIntro: false});
        },
        fullscreen() {
            this.updateContainerSize();
            if (!this.viewer) {
                return;
            }
            if (this.fullscreen) {
                const targetHfov = this.modeAR ? AR_TARGET_HFOV : FULLSCREEN_DEFAULT_HFOV;
                this.viewer.setHfov(targetHfov, true);
            }
        },
        modeAr(newValue) {
            const nextModeAr = Boolean(newValue);
            if (nextModeAr === this.modeAR) {
                return;
            }
            this.modeAR = nextModeAr;
            if (this.modeAR) {
                this.activateArMode();
                return;
            }
            this.deactivateArMode();
        },
        gridModeGlyph() {
            this.updateHotspots({force: true});
        },
    }
}
</script>

<style lang="scss" scoped>
:deep(.pnlm-hotspot-base) {
    &.pano-custom-hotspot {
        width: 100px;
        height: 100px;
        border: none;
        box-shadow: none;
        background: transparent !important;
        pointer-events: none;
        overflow: visible;
    }

    .pano-custom-hotspot-wrapper {
        width: 100px;
        height: 100px;
        display: flex;
        align-items: center;
        justify-content: center;
        perspective: 120px;
    }

    .pano-custom-hotspot-disc {
        width: 90px;
        height: 90px;
        border-radius: 50%;
        border: 5px solid rgba(255, 255, 255, 0.9);
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        transform: rotateX(50deg);
        transform-style: preserve-3d;
        box-sizing: border-box;
    }

    .pano-custom-hotspot-chevron {
        width: 28px;
        height: 28px;
        border-top: 8px solid #fff;
        border-right: 8px solid #fff;
        border-bottom: none;
        border-radius: 2px;
        border-left: none;
        transform: rotate(-45deg) translate(-6px, 6px);
        filter: drop-shadow(-3px 3px 1px rgba(0, 0, 0, 0.4));

        &.is-flipped {
            transform: rotate(135deg);
            filter: drop-shadow(3px -3px 1px rgba(0, 0, 0, 0.4));
        }
    }

    .pano-custom-hotspot-arrow-head {
        width: 44px;
        height: auto;
        display: block;
        position: relative;
        z-index: 3;
        transform-origin: center;
        filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.4));
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;
        pointer-events: none;

        &.is-flipped {
            transform: translateY(12px) rotate(180deg) translateZ(2px);
            filter: drop-shadow(0 -4px 3px rgba(0, 0, 0, 0.4));
        }
    }

    .pano-custom-hotspot-location-icon {
        width: 72px;
        height: 72px;
        position: absolute;
        left: 50%;
        top: 50%;
        z-index: 5;
        transform: var(--pano-location-transform, translate(-50%, -50%) translateY(-52px));
        display: block;
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;
        pointer-events: none;
    }

    &.pano-custom-hotspot-arrived .pano-custom-hotspot-disc {
        animation: ground-arrow-pulse 2s ease-in-out infinite;
    }

    &.pano-custom-hotspot-main {
        pointer-events: auto;
        cursor: pointer;
    }

    &.pano-custom-hotspot-main-pulse .pano-custom-hotspot-disc {
        animation: hotspot-main-pulse 1.8s ease-in-out infinite;
    }

}

.pano-360.pano-360-ar-mode {
    :deep(.pnlm-hotspot-base.pano-custom-hotspot-main) {
        pointer-events: none;
        cursor: default;
    }

    :deep(.pnlm-hotspot-base.pano-custom-hotspot-guide) {
        pointer-events: none;
    }
}

:deep(.pnlm-hotspot-base) {
    &.pano-custom-hotspot-guide {
        width: 100px;
        height: 100px;
    }
}

:deep(.pnlm-hotspot-base) {
    &.pano-custom-hotspot-side {
        width: 100px;
        height: 100px;
    }
}

:deep(.pnlm-hotspot-base) {
    &.pano-grid-only {
        width: 96px;
        height: 96px;
        border: none;
        box-shadow: none;
        background: transparent !important;
        pointer-events: none;
        overflow: visible;
    }

    .pano-grid-only-wrapper {
        width: 96px;
        height: 96px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .pano-grid-only-marker {
        --grid-only-color: #2297ff;
        --grid-only-color-soft: rgba(34, 151, 255, 0.18);
        --grid-only-color-glow: rgba(34, 151, 255, 0.42);
        width: 74px;
        height: 74px;
        border-radius: 999px;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        background:
            radial-gradient(circle at center, rgba(255, 255, 255, 0.98) 0 38%, transparent 39%),
            radial-gradient(circle at center, var(--grid-only-color-soft) 0 56%, transparent 57%),
            linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(255, 255, 255, 0.78));
        border: 2px solid rgba(255, 255, 255, 0.96);
        box-shadow:
            0 0 0 8px var(--grid-only-color-soft),
            0 0 22px 6px var(--grid-only-color-glow),
            0 10px 22px rgba(0, 0, 0, 0.28);
        animation: grid-only-pulse 1.8s ease-in-out infinite;
    }

    .pano-grid-only-ring {
        position: absolute;
        inset: 14px;
        border-radius: 999px;
        border: 3px solid var(--grid-only-color);
        box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9);
    }

    .pano-grid-only-icon {
        color: var(--grid-only-color);
        font-size: 30px;
        line-height: 1;
        position: relative;
        z-index: 1;
        filter: drop-shadow(0 1px 1px rgba(255, 255, 255, 0.8));
    }
}

@keyframes ground-arrow-pulse {
    0%, 100% {
        box-shadow: 0 0 14px 2px rgba(34, 151, 255, 0.22), 0 8px 10px 2px rgba(0, 0, 0, 0.38);
    }
    50% {
        box-shadow: 0 0 20px 5px rgba(34, 151, 255, 0.4), 0 8px 10px 2px rgba(0, 0, 0, 0.38);
    }
}

@keyframes side-indicator-pulse {
    0%, 100% {
        transform: scale(0.95);
        box-shadow: 0 0 12px 3px rgba(34, 151, 255, 0.28);
    }
    50% {
        transform: scale(1.05);
        box-shadow: 0 0 22px 8px rgba(34, 151, 255, 0.46);
    }
}

@keyframes hotspot-main-pulse {
    0%, 100% {
        transform: rotateX(50deg) scale(0.95);
        box-shadow: 0 0 10px 2px rgba(34, 151, 255, 0.24), 0 8px 10px 2px rgba(0, 0, 0, 0.38);
    }
    50% {
        transform: rotateX(50deg) scale(1.05);
        box-shadow: 0 0 16px 4px rgba(34, 151, 255, 0.38), 0 8px 10px 2px rgba(0, 0, 0, 0.38);
    }
}

@keyframes grid-only-pulse {
    0%, 100% {
        transform: scale(0.96);
        box-shadow:
            0 0 0 8px rgba(34, 151, 255, 0.16),
            0 0 18px 4px rgba(34, 151, 255, 0.24),
            0 10px 22px rgba(0, 0, 0, 0.24);
    }
    50% {
        transform: scale(1.04);
        box-shadow:
            0 0 0 12px rgba(34, 151, 255, 0.2),
            0 0 28px 8px rgba(34, 151, 255, 0.34),
            0 12px 24px rgba(0, 0, 0, 0.28);
    }
}

:deep(.pnlm-about-msg) {
    display: none;
}

.pano-360 {
    position: relative;
    width: var(--instruction-image-width);
    height: var(--instruction-image-height);
    padding: 0;
    border: none;

    &.pano-360-preview {
        width: 100%;
        height: 100%;
    }

    .pano-360-container {
        width: inherit;
        height: inherit;
        position: relative;
    }

    .pano-loading-overlay {
        position: absolute;
        inset: 0;
        z-index: 20;
        background: #000;
        pointer-events: none;
    }

    .pano-flying-arrow {
        position: absolute;
        left: 0;
        top: 0;
        z-index: 7;
        pointer-events: none;
        will-change: transform, opacity;
    }

    .pano-flying-arrow-wrapper {
        width: 100px;
        height: 100px;
        display: flex;
        align-items: center;
        justify-content: center;
        perspective: 120px;
    }

    .pano-flying-arrow-disc {
        width: 90px;
        height: 90px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        transform: rotateX(50deg);
        transform-style: preserve-3d;
    }

    .pano-flying-arrow-head {
        width: 44px;
        height: auto;
        display: block;
        transform: translateY(-2px);
        filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.4));
        pointer-events: none;
    }

    .pano-side-indicators {
        position: absolute;
        inset: 0;
        z-index: 6;
        pointer-events: none;
        opacity: 0;
        transition: opacity 220ms ease;

        &.is-visible {
            opacity: 1;
        }
    }

    .pano-side-indicator {
        position: absolute;
        top: 50%;
        width: 96px;
        height: 96px;
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        justify-content: center;
        perspective: 120px;
        pointer-events: none;
        cursor: pointer;

        &.is-left {
            left: 40px;
        }

        &.is-right {
            right: 40px;
        }
    }

    .pano-side-indicator-disc {
        width: 84px;
        height: 84px;
        border-radius: 50%;
        position: relative;
        animation: side-indicator-pulse 1.8s ease-in-out infinite;
    }

    .pano-side-indicator-chevron {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 22px;
        height: 22px;
        border-top: 7px solid #fff;
        border-right: 7px solid #fff;
        border-left: none;
        border-bottom: none;
        border-radius: 2px;
        filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.35));
    }

    .pano-side-indicator.is-left .pano-side-indicator-chevron {
        transform: translate(calc(-50% - 7px), -50%) rotate(-135deg);
    }

    .pano-side-indicator.is-left .pano-side-indicator-chevron.is-trail {
        transform: translate(calc(-50% + 10px), -50%) rotate(-135deg);
        opacity: 0.82;
    }

    .pano-side-indicator.is-right .pano-side-indicator-chevron {
        transform: translate(calc(-50% + 7px), -50%) rotate(45deg);
    }

    .pano-side-indicator.is-right .pano-side-indicator-chevron.is-trail {
        transform: translate(calc(-50% - 10px), -50%) rotate(45deg);
        opacity: 0.82;
    }

    .debug {
        position: absolute;
        left: 0;
        top: 0;
        backdrop-filter: blur(5px) contrast(0.8);
        background-color: rgba(white, 0.8);
        padding: 3px 6px;
        border-bottom-right-radius: 8px;

        .badge {
            display: block;
        }
    }

    &.fullscreen {
        position: fixed;
        top: 0;
        right: 0;
        bottom: 70px;
        left: 0;
        width: 100%;
        height: calc(var(--app-height) - 120px);
        z-index: 1000;
    }

    .animated-finger {
        cursor: pointer;
        animation: flying-finger 4s 2;
        bottom: 50px;
        right: 50px;
        opacity: 0;
        transform: translate(-50%, -50%);
        padding: 10px;
        height: 150px;
        width: auto;
        position: absolute;
        pointer-events: none;
    }
}

@keyframes flying-finger {
    0% {
        opacity: 0;
        transform: translateY(60px) translateX(0px);
    }
    30% {
        opacity: 1;
        transform: translateY(40px) translateX(0px);
    }
    60% {
        opacity: 1;
        transform: translateY(40px) translateX(-40px);
    }
    100% {
        opacity: 0;
        transform: translateY(60px) translateX(-40px);
    }
}

</style>



